import React, { useCallback, useMemo } from 'react';
import Helmet from 'react-helmet';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Header } from '../../components';
import { TabBar, TabBarItem } from '../../components/design-system';
import { AUTH_QUERY } from '../../gql';
import { UserSettingsForm } from '../../components/UserSettingsForm';
import { AccessTokenSettings } from '../../components/AccessTokenSettings';
import { AccountSkeleton } from './AccountSkeleton';
import { SecuritySettings } from './SecuritySettings';

type AccountTab = 'account' | 'api' | 'security';

const pageStyles = { width: '100%', minHeight: '100%' };
const contentStyles = { pb: 6 };
const headingStyles = { fontWeight: 700 };
const descriptionStyles = { color: 'text.secondary' };
const sectionStyles = { maxWidth: 900 };

const Account = React.memo(() => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading } = useQuery(AUTH_QUERY, { fetchPolicy: 'cache-only' });
  const user = data?.viewer.user;
  const activeTab: AccountTab = location.pathname.endsWith('/api')
    ? 'api'
    : location.pathname.endsWith('/security')
    ? 'security'
    : 'account';
  const tabs = useMemo<TabBarItem[]>(
    () => [
      {
        label: intl.formatMessage({
          id: 'account.tabs.account',
          defaultMessage: 'Account',
        }),
        value: 'account',
      },
      {
        label: intl.formatMessage({
          id: 'account.tabs.api',
          defaultMessage: 'API',
        }),
        value: 'api',
      },
      {
        label: intl.formatMessage({
          id: 'account.tabs.security',
          defaultMessage: 'Security',
        }),
        value: 'security',
      },
    ],
    [intl]
  );
  const handleTabChange = useCallback(
    (value: string) => {
      const paths: Record<AccountTab, string> = {
        account: '/account',
        api: '/account/api',
        security: '/account/security',
      };

      navigate(paths[value as AccountTab]);
    },
    [navigate]
  );

  if (loading || !user) {
    return <AccountSkeleton />;
  }

  return (
    <div style={pageStyles}>
      <Helmet
        title={intl.formatMessage({
          id: 'account.pageTitle',
          defaultMessage: 'Account | Acolyte',
        })}
      />
      <Header />
      <Container maxWidth="xl">
        <Stack gap={2.5} sx={contentStyles}>
          <Box>
            <Typography component="h1" variant="h4" sx={headingStyles}>
              <FormattedMessage
                id="account.title"
                defaultMessage="Account"
              />
            </Typography>
            <Typography variant="body2" sx={descriptionStyles}>
              <FormattedMessage
                id="account.description"
                defaultMessage="Manage your profile, API keys, and active sessions."
              />
            </Typography>
          </Box>

          <TabBar
            aria-label={intl.formatMessage({
              id: 'account.sections',
              defaultMessage: 'Account sections',
            })}
            items={tabs}
            onChange={handleTabChange}
            value={activeTab}
          />

          <Stack gap={2} sx={sectionStyles}>
            <Routes>
              <Route
                index
                element={
                  <>
                    <Typography component="h2" variant="h5" fontWeight={700}>
                      <FormattedMessage
                        id="account.settings.title"
                        defaultMessage="Account settings"
                      />
                    </Typography>
                    <UserSettingsForm user={user} />
                  </>
                }
              />
              <Route
                path="api"
                element={
                  <>
                    <Typography component="h2" variant="h5" fontWeight={700}>
                      <FormattedMessage
                        id="account.apiKeys.title"
                        defaultMessage="API keys"
                      />
                    </Typography>
                    <AccessTokenSettings />
                  </>
                }
              />
              <Route
                path="security"
                element={
                  <>
                    <Box>
                      <Typography
                        component="h2"
                        variant="h5"
                        fontWeight={700}
                      >
                        <FormattedMessage
                          id="account.security.title"
                          defaultMessage="Active sessions"
                        />
                      </Typography>
                      <Typography variant="body2" sx={descriptionStyles}>
                        <FormattedMessage
                          id="account.security.description"
                          defaultMessage="Review the devices currently signed in to your account."
                        />
                      </Typography>
                    </Box>
                    <SecuritySettings />
                  </>
                }
              />
              <Route path="*" element={<Navigate replace to="/account" />} />
            </Routes>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
});

Account.displayName = 'Account';

export { Account };
